import { createContext, useContext, useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';

const MessageCacheContext = createContext(null);

// eslint-disable-next-line react-refresh/only-export-components
export const useMessageCache = () => {
  const context = useContext(MessageCacheContext);
  if (!context) {
    throw new Error('useMessageCache must be used within a MessageCacheProvider');
  }
  return context;
};

const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const STORAGE_KEY_PREFIX = 'pplkpr_message_cache_';

export const MessageCacheProvider = ({ children }) => {
  const [cache, setCache] = useState(new Map());
  const cacheTimestamps = useRef(new Map());

  // Generate cache key from parameters
  const getCacheKey = useCallback((params) => {
    const { sortMode, filterCategory, lastDocId } = params;
    return `${sortMode}-${filterCategory || 'all'}-${lastDocId || 'initial'}`;
  }, []);

  // Check if cache entry is still valid
  const isCacheValid = useCallback((key) => {
    const timestamp = cacheTimestamps.current.get(key);
    if (!timestamp) return false;
    return Date.now() - timestamp < CACHE_TTL;
  }, []);

  // Get cached messages
  const getCachedMessages = useCallback((params) => {
    const key = getCacheKey(params);
    
    // Check memory cache first
    if (cache.has(key) && isCacheValid(key)) {
      console.log('Cache hit (memory):', key);
      return cache.get(key);
    }

    // Check localStorage as fallback
    try {
      const storageKey = STORAGE_KEY_PREFIX + key;
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const { data, timestamp } = JSON.parse(stored);
        if (Date.now() - timestamp < CACHE_TTL) {
          console.log('Cache hit (localStorage):', key);
          // Restore to memory cache
          setCache(prev => new Map(prev).set(key, data));
          cacheTimestamps.current.set(key, timestamp);
          return data;
        }
      }
    } catch (error) {
      console.warn('Error reading from localStorage:', error);
    }

    console.log('Cache miss:', key);
    return null;
  }, [cache, getCacheKey, isCacheValid]);

  // Store messages in cache
  const setCachedMessages = useCallback((params, data) => {
    const key = getCacheKey(params);
    const timestamp = Date.now();

    // Update memory cache
    setCache(prev => {
      const newCache = new Map(prev);
      newCache.set(key, data);
      return newCache;
    });
    cacheTimestamps.current.set(key, timestamp);

    // Store in localStorage
    try {
      const storageKey = STORAGE_KEY_PREFIX + key;
      localStorage.setItem(storageKey, JSON.stringify({ data, timestamp }));
    } catch (error) {
      console.warn('Error writing to localStorage:', error);
    }

    console.log('Cache set:', key);
  }, [getCacheKey]);

  // Invalidate specific cache entry
  const invalidateCache = useCallback((params) => {
    const key = getCacheKey(params);
    
    // Remove from memory cache
    setCache(prev => {
      const newCache = new Map(prev);
      newCache.delete(key);
      return newCache;
    });
    cacheTimestamps.current.delete(key);

    // Remove from localStorage
    try {
      const storageKey = STORAGE_KEY_PREFIX + key;
      localStorage.removeItem(storageKey);
    } catch (error) {
      console.warn('Error removing from localStorage:', error);
    }

    console.log('Cache invalidated:', key);
  }, [getCacheKey]);

  // Clear all cache
  const clearAllCache = useCallback(() => {
    // Clear memory cache
    setCache(new Map());
    cacheTimestamps.current.clear();

    // Clear localStorage
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(STORAGE_KEY_PREFIX)) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.warn('Error clearing localStorage:', error);
    }

    console.log('All cache cleared');
  }, []);

  // Invalidate caches for a specific sort mode (all categories and pages)
  const invalidateSortMode = useCallback((sortMode) => {
    setCache(prev => {
      const newCache = new Map(prev);
      const keysToDelete = [];
      
      newCache.forEach((_, key) => {
        if (key.startsWith(`${sortMode}-`)) {
          keysToDelete.push(key);
        }
      });
      
      keysToDelete.forEach(key => {
        newCache.delete(key);
        cacheTimestamps.current.delete(key);
        
        // Also remove from localStorage
        try {
          localStorage.removeItem(STORAGE_KEY_PREFIX + key);
        } catch (error) {
          console.warn('Error removing from localStorage:', error);
        }
      });
      
      return newCache;
    });

    console.log('Invalidated all caches for sort mode:', sortMode);
  }, []);

  const value = {
    getCachedMessages,
    setCachedMessages,
    invalidateCache,
    clearAllCache,
    invalidateSortMode,
  };

  return (
    <MessageCacheContext.Provider value={value}>
      {children}
    </MessageCacheContext.Provider>
  );
};

MessageCacheProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
